import { useMemo } from "react";
// MUI
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";

// External Components
import Masonry from "react-masonry-css";

// Components
import CollapsibleCard from "@/Layouts/components/Cards/CollapsibleCard";
import ObjectDetails from "./ObjectDetails";

// Helpers
import { getItemTitleFromTitle } from "./helpers";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

interface IArrayDialogProps {
	handleOnCloseDialog: () => void;
	dialogContent: {
		title?: string;
		data: object[];
	} | null;
}

const MAX_LENGTH = 4;

const ArrayDialog = (props: IArrayDialogProps) => {
	const { handleOnCloseDialog, dialogContent } = props;
	const { title = "", data = [] } = dialogContent ?? {};

	if (!dialogContent) {
		return null;
	}

	const sanitizedTitle = useMemo(() => title.trim().replace("%br%", ""), [title]);
	const singularTitle = useMemo(() => getItemTitleFromTitle(sanitizedTitle), [sanitizedTitle]);
	const lengthRow = useMemo(() => Math.min(data.length, MAX_LENGTH), [data.length]);

	const itemsList = useMemo(
		() =>
			data.map((item, idx) => {
				const key = `key-${idx}`;
				return (
					<Box key={key} width={300} className="!mb-0">
						<CollapsibleCard
							customTitle={`${singularTitle} ${idx + 1}`}
							customTitleClasses="!font-montserrat !text-[14px] !ml-0 !font-medium"
							expanded
							addClassName={`!w-[300px]`}
							innerClass={"!p-0"}
						>
							<ObjectDetails item={item} />
						</CollapsibleCard>
					</Box>
				);
			}),
		[data, singularTitle]
	);

	return (
		<Dialog
			classes={{
				paper: "!rounded-[10px] !shadow-md p-4",
			}}
			open={dialogContent !== null}
			onClose={handleOnCloseDialog}
			PaperProps={{
				style: {
					maxWidth: "1400px",
					maxHeight: "75vh",
				},
			}}
			disableEscapeKeyDown
			onDoubleClick={(e) => {
				e.stopPropagation();
			}}
		>
			<DialogTitle>
				<Typography className={"!font-montserrat !text-lg !font-medium !text-default"}>{sanitizedTitle}</Typography>
				<IconButton className="!absolute !right-1 !top-1 !text-default" aria-label="close" onClick={handleOnCloseDialog}>
					<CloseRoundedIcon fontSize="medium" />
				</IconButton>
			</DialogTitle>
			<DialogContent className="!mb-0">
				<Masonry breakpointCols={lengthRow} cellSpacing={3} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
					{itemsList}
				</Masonry>
			</DialogContent>
		</Dialog>
	);
};

export default ArrayDialog;
